import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Locale, Translations } from './types';
import zhTranslations from './locales/zh';
import enTranslations from './locales/en';

const translations: Record<Locale, Translations> = {
  zh: zhTranslations,
  en: enTranslations,
};

interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Translations;
}

const I18nContext = createContext<I18nContextType | null>(null);

// Get initial locale from URL path
function getLocaleFromPath(path: string): Locale {
  if (path.startsWith('/en')) return 'en';
  return 'zh';
}

// Get saved locale from localStorage
function getSavedLocale(): Locale | null {
  if (typeof window === 'undefined') return null;
  const saved = localStorage.getItem('webclip-lang');
  if (saved === 'zh' || saved === 'en') return saved;
  return null;
}

// Re-export for potential use elsewhere
export { getSavedLocale };

interface I18nProviderProps {
  children: ReactNode;
  initialLocale?: Locale;
}

export function I18nProvider({ children, initialLocale }: I18nProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();

  // Initialize locale from URL path, or saved preference, or default to zh
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (initialLocale) return initialLocale;
    return getLocaleFromPath(location.pathname);
  });

  // Update locale when URL changes
  useEffect(() => {
    const newLocale = getLocaleFromPath(location.pathname);
    setLocaleState(newLocale);
    // Update html lang attribute
    document.documentElement.lang = newLocale === 'zh' ? 'zh-CN' : 'en';
  }, [location.pathname]);

  const setLocale = useCallback((newLocale: Locale) => {
    const currentPath = location.pathname;
    const currentSearch = location.search;

    // Calculate new path
    let newPath: string;
    if (newLocale === 'en') {
      // Add /en prefix if not present
      newPath = currentPath.startsWith('/en') ? currentPath : `/en${currentPath}`;
    } else {
      // Remove /en prefix if present
      newPath = currentPath.replace(/^\/en/, '') || '/';
    }

    // Save preference
    localStorage.setItem('webclip-lang', newLocale);

    // Navigate to new URL
    navigate(newPath + currentSearch, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const value = {
    locale,
    setLocale,
    t: translations[locale],
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextType {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}

// Hook for server-side rendering
export function getLocaleFromRequest(url: string): Locale {
  return getLocaleFromPath(new URL(url).pathname);
}

export { translations };
export type { Locale, Translations };
